import { Listbox } from '@headlessui/react';
import { Fragment, useEffect } from 'react';
import clsx from 'clsx';
import { useField } from 'formik';
import { ICategoriesResponseData } from '../state/models/CategoriesResponse.ts';
import { useTranslation } from 'react-i18next';

type CustomSelectType = {
    title?: string;
    arr: Array<ICategoriesResponseData>;
    formikValue: string;
    multiple?: boolean;
    existLabel?: boolean;
    setFieldCategoryMode?: (mode: 'no_adult' | 'custom' | 'all') => void;
};

export const MultipleSelect = ({
                                   title,
                                   arr,
                                   formikValue,
                                   existLabel = true,
                                   multiple = false,
                                   setFieldCategoryMode,
                               }: CustomSelectType) => {
    const [field, meta, helpers] = useField<string[]>(formikValue);
    const { t } = useTranslation();
    const hasError = !!(meta.touched && meta.error);

    const clearError = () => {
        helpers.setTouched(false);
        helpers.setError(undefined);
    };

    // Найдём категорию с флагом adult (slug === 'adult') и все остальные
    const adultCategory = arr.find(item => item.slug === 'adult');
    const otherCategories = arr.filter(item => item.slug !== 'adult');

    // Текущий массив выбранных id
    const selectedIds: string[] = field.value || [];

    // При первой загрузке, если ничего не выбрано, проставляем "все, кроме adult"
    useEffect(() => {
        if (!field.value || field.value.length === 0) {
            const defaultIds = otherCategories.map(cat => cat.id);
            helpers.setValue(defaultIds);
            if (setFieldCategoryMode) {
                setFieldCategoryMode('no_adult');
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [arr]);

    // Варианты состояний:
    const isAllSelected = arr.every(cat => selectedIds.includes(cat.id));
    const isAllExceptAdult =
        otherCategories.every(cat => selectedIds.includes(cat.id)) &&
        !selectedIds.includes(adultCategory?.id || '');

    // Композиция строкового представления выбранных
    const selectedCategories = arr.filter(item => selectedIds.includes(item.id));

    const items = (() => {
        if (isAllSelected) return t('categories.all');
        if (isAllExceptAdult) return t('categories.all_except_adult');

        const names = selectedCategories.map(item => t(`categories.${item.slug}`));
        const visibleNames = names.slice(0, 3);
        const restCount = names.length - visibleNames.length;

        return restCount > 0
            ? `${visibleNames.join(', ')} ${t('categories.and_others', { count: restCount })}`
            : visibleNames.join(', ');
    })();

    // Функция, которая реагирует на изменение списка id
    const handleChange = (newSelected: string[]) => {
        clearError();
        helpers.setValue(newSelected);

        if (!setFieldCategoryMode) return;

        // Если выбраны абсолютно все
        if (newSelected.length === arr.length) {
            setFieldCategoryMode('all');
            return;
        }
        // Если выбраны все, кроме adult
        if (
            otherCategories.every(cat => newSelected.includes(cat.id)) &&
            !newSelected.includes(adultCategory?.id || '')
        ) {
            setFieldCategoryMode('no_adult');
            return;
        }
        // Иначе — пользователь сам выбрал/снял
        setFieldCategoryMode('custom');
    };

    // Кнопка "Выбрать все" / "Очистить все"
    const handleToggleAll = () => {
        clearError();

        if (isAllSelected) {
            // Если выбраны все — очищаем всё
            helpers.setValue([]);
            setFieldCategoryMode?.('custom'); // или 'none' если хочешь
        } else {
            // В любом другом случае — выбрать все
            const allIds = arr.map(cat => cat.id);
            helpers.setValue(allIds);
            setFieldCategoryMode?.('all');
        }
    };

    return (
        <div className="w-full relative">
            {existLabel && (
                <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-semibold text-[var(--text-color-dark)]">
                        {title}
                    </label>
                    <button
                        type="button"
                        onClick={handleToggleAll}
                        className="text-sm text-[var(--text-dark-blue)] hover:underline"
                    >
                        {isAllSelected ? t('categories.clear_all') : t('categories.select_all')}
                    </button>
                </div>
            )}

            <Listbox value={selectedIds} onChange={handleChange} multiple={multiple}>
                <div className="relative">
                    <Listbox.Button
                        className={clsx(
                            'relative w-full cursor-default rounded-lg bg-white border py-[7px] pl-3 pr-10 text-left shadow-xs focus:outline-none text-sm',
                            {
                                '!border-red-500': hasError,
                                '!border-[var(--btn-border-custom)]': !hasError,
                            }
                        )}
                    >
                        {items || t('categories.placeholder')}
                    </Listbox.Button>

                    <Listbox.Options
                        className="absolute flex flex-col gap-1 z-10 list-none mt-1 max-h-60 w-full overflow-auto rounded-md bg-[var(--select-bg)] py-1 px-1 focus:outline-none sm:text-[10px] text-white custom-scrollBar border border-[var(--background-line)] shadow-sm"
                    >
                        {arr.map(item => {
                            const selected = selectedIds.includes(item.id);

                            return (
                                <Listbox.Option key={item.id} value={item.id} as={Fragment}>
                                    {({ active }) => (
                                        <li
                                            className={clsx(
                                                'cursor-default select-none !rounded-[6px] relative py-1 px-2 text-xs pl-6 text-[var(--color-listItem-profile)] flex gap-1 items-center',
                                                {
                                                    'bg-[var(--background-select-active)] !text-white': active,
                                                    '!font-medium': selected,
                                                }
                                            )}
                                        >
                                            {selected && (
                                                <span className="absolute left-1.5">
                          <svg
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                              strokeWidth="3"
                              stroke="currentColor"
                              className="w-[14px] h-[13px]"
                          >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                d="m4.5 12.75 6 6 9-13.5"
                            />
                          </svg>
                        </span>
                                            )}
                                            {t(`categories.${item.slug}`)}
                                        </li>
                                    )}
                                </Listbox.Option>
                            );
                        })}
                    </Listbox.Options>
                </div>
            </Listbox>

            {hasError && (
                <p className="mt-1 text-xs text-red-500 absolute bottom-[-18px]">{meta.error}</p>
            )}
        </div>
    );
};
